import React from 'react';
import Layout from '@/components/layout/Layout';
import { SEOHead } from '@/components/shared/SEOHead';
import { Link } from 'react-router-dom';
import { Shield } from 'lucide-react';

const PrivacyPolicyPage = () => {
  const schema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy",
    "description": "Privacy Policy for Redspire Civil Engineering - Learn how we collect, use, and protect your personal information.",
    "url": "https://redspire.com/privacy-policy",
    "publisher": {
      "@type": "Organization",
      "name": "Redspire Civil Engineering"
    },
    "datePublished": "2025-01-01",
    "dateModified": "2025-01-01"
  };

  return (
    <Layout>
      <SEOHead
        title="Privacy Policy | Redspire Civil Engineering"
        description="Learn how Redspire Civil Engineering collects, uses, and protects your personal information. We are committed to safeguarding your privacy."
        keywords={['privacy policy', 'data protection', 'personal information', 'Redspire privacy']}
        url="https://redspire.com/privacy-policy"
        schema={schema}
      />

      <div className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4 mb-4">
            <Shield className="w-12 h-12 text-red-500" />
            <h1 className="text-4xl md:text-5xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-xl text-gray-300">Your privacy is important to us</p>
          <p className="text-sm text-gray-400 mt-4">Effective Date: January 1, 2025</p>
        </div>
      </div>

      <article className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg max-w-none">
          
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Redspire Civil Engineering ("Redspire," "we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, engage our services, or communicate with us. By accessing or using our services, you consent to the practices described in this Privacy Policy.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This policy applies to information we collect through our website (https://redspire.com), email, phone, in-person meetings, and any other means of communication with Redspire.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">2.1 Personal Information</h3>
            <p className="text-gray-700 leading-relaxed">
              We may collect personal information that you voluntarily provide to us, including but not limited to:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>Full name, email address, phone number</li>
              <li>Company name, job title, business address</li>
              <li>Project details, technical requirements, and specifications</li>
              <li>Payment and billing information</li>
              <li>Professional credentials and licenses (for partners and contractors)</li>
              <li>Communications, correspondence, and inquiries</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">2.2 Automatically Collected Information</h3>
            <p className="text-gray-700 leading-relaxed">
              When you visit our website, we automatically collect certain information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>IP address, browser type, and operating system</li>
              <li>Pages viewed, time spent on pages, and navigation paths</li>
              <li>Referring website and search terms used to find our site</li>
              <li>Device information and unique identifiers</li>
              <li>Cookies and similar tracking technologies (see Section 7)</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">2.3 Professional Information</h3>
            <p className="text-gray-700 leading-relaxed">
              For clients and project stakeholders, we may collect technical and project-related information including engineering documents, site assessments, geotechnical reports, and project communications necessary to provide our civil engineering services.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We use the information we collect for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Service Delivery:</strong> To provide civil engineering services, project management, consulting, and technical support</li>
              <li><strong>Communication:</strong> To respond to inquiries, provide project updates, and maintain client relationships</li>
              <li><strong>Business Operations:</strong> To process contracts, invoices, and payments</li>
              <li><strong>Compliance:</strong> To meet legal, regulatory, and professional obligations including engineering standards and licensing requirements</li>
              <li><strong>Improvement:</strong> To analyze website usage, improve our services, and develop new offerings</li>
              <li><strong>Marketing:</strong> To send newsletters, project showcases, and industry updates (with your consent)</li>
              <li><strong>Security:</strong> To protect against fraud, unauthorized access, and security threats</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">4. Information Sharing and Disclosure</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              We do not sell, rent, or trade your personal information. We may share your information in the following circumstances:
            </p>
            
            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.1 Service Providers</h3>
            <p className="text-gray-700 leading-relaxed">
              We may share information with trusted third-party service providers who assist us in operating our business, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>Subconsultants and engineering specialists</li>
              <li>Laboratory testing facilities and materials testing services</li>
              <li>IT service providers, cloud hosting, and data storage</li>
              <li>Payment processors and financial institutions</li>
              <li>Legal, accounting, and professional services</li>
            </ul>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.2 Legal Requirements</h3>
            <p className="text-gray-700 leading-relaxed">
              We may disclose your information when required by law, court order, subpoena, or government regulation, or to protect our rights, property, or safety, or that of others.
            </p>

            <h3 className="text-2xl font-semibold text-gray-800 mb-3 mt-6">4.3 Business Transfers</h3>
            <p className="text-gray-700 leading-relaxed">
              In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity, subject to the same privacy protections.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">5. Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement industry-standard security measures to protect your information, including:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mt-3">
              <li>Encryption of data in transit and at rest</li>
              <li>Secure servers and firewalls</li>
              <li>Access controls and authentication protocols</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Employee training on data protection and confidentiality</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              While we strive to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but will notify you of any material breach as required by law.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Your Rights and Choices</h2>
            <p className="text-gray-700 leading-relaxed mb-4">
              You have the following rights regarding your personal information:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information (subject to legal retention requirements)</li>
              <li><strong>Opt-Out:</strong> Unsubscribe from marketing communications at any time</li>
              <li><strong>Restriction:</strong> Request limitation on how we use your information</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service provider</li>
              <li><strong>Objection:</strong> Object to processing of your information for certain purposes</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-4">
              To exercise these rights, please contact us at info@redspire.com or +1 (203) 496-3375.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Cookies and Tracking Technologies</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website uses cookies and similar technologies to enhance your experience, analyze usage, and provide personalized content. You can control cookie preferences through your browser settings. For detailed information, please see our <Link to="/cookie-policy" className="text-red-600 hover:underline">Cookie Policy</Link>.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites, including project partners, industry associations, and resource sites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies before providing any personal information.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
            <p className="text-gray-700 leading-relaxed">
              We retain your personal information for as long as necessary to fulfill the purposes outlined in this Privacy Policy, unless a longer retention period is required or permitted by law. Project-related information is retained in accordance with professional engineering standards and applicable regulations, typically for a minimum of 7-10 years after project completion.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have inadvertently collected information from a child, please contact us immediately.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">11. California Privacy Rights</h2>
            <p className="text-gray-700 leading-relaxed">
              California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to know what personal information is collected, the right to delete personal information, and the right to opt-out of the sale of personal information. Redspire does not sell personal information. To exercise your CCPA rights, contact us at info@redspire.com.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">12. International Data Transfers</h2>
            <p className="text-gray-700 leading-relaxed">
              Our primary operations are based in the United States. If you are accessing our services from outside the U.S., please be aware that your information may be transferred to, stored, and processed in the United States where our servers and databases are located.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of material changes by posting the updated policy on our website with a new "Effective Date." We encourage you to review this policy periodically.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">14. Contact Information</h2>
            <p className="text-gray-700 leading-relaxed">
              If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 p-6 rounded-lg mt-4">
              <p className="text-gray-900 font-semibold mb-2">Redspire Civil Engineering</p>
              <p className="text-gray-700">218 Brookside Industrial Park</p>
              <p className="text-gray-700">Stamford, CT 06902</p>
              <p className="text-gray-700 mt-3">Email: <a href="mailto:info@redspire.com" className="text-red-600 hover:underline">info@redspire.com</a></p>
              <p className="text-gray-700">Phone: <a href="tel:+12034963375" className="text-red-600 hover:underline">+1 (203) 496-3375</a></p>
            </div>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-12">
            <p className="text-sm text-gray-600">
              Last Updated: January 1, 2025
            </p>
            <p className="text-sm text-gray-600 mt-2">
              © 2025 Redspire Civil Engineering. All rights reserved.
            </p>
          </div>

        </div>
      </article>
    </Layout>
  );
};

export default PrivacyPolicyPage;
